import { supabase } from './supabase';

// POS & Inventory Service
export const posService = {
  // Categories
  async getCategories(gymId) {
    try {
      const { data, error } = await supabase?.from('pos_categories')?.select('*')?.eq('gym_id', gymId)?.eq('is_active', true)?.order('display_order');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createCategory(gymId, categoryData) {
    try {
      const { data, error } = await supabase?.from('pos_categories')?.insert({ gym_id: gymId, ...categoryData })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateCategory(categoryId, updates) {
    try {
      const { data, error } = await supabase?.from('pos_categories')?.update(updates)?.eq('id', categoryId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteCategory(categoryId) {
    try {
      const { error } = await supabase?.from('pos_categories')?.update({ is_active: false })?.eq('id', categoryId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Products
  async getProducts(gymId, filters = {}) {
    try {
      let query = supabase?.from('pos_products')?.select(`
          *,
          category:pos_categories(id, name)
        `)?.eq('gym_id', gymId)?.eq('is_active', true);

      if (filters?.categoryId) {
        query = query?.eq('category_id', filters?.categoryId);
      }

      if (filters?.searchTerm) {
        query = query?.or(`name.ilike.%${filters?.searchTerm}%,barcode.ilike.%${filters?.searchTerm}%,alu.ilike.%${filters?.searchTerm}%`);
      }

      const { data, error } = await query?.order('name');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getProductByBarcode(gymId, barcode) {
    try {
      const { data, error } = await supabase?.from('pos_products')?.select(`
          *,
          category:pos_categories(id, name)
        `)?.eq('gym_id', gymId)?.eq('barcode', barcode)?.eq('is_active', true)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getProductByALU(gymId, alu) {
    try {
      const { data, error } = await supabase?.from('pos_products')?.select(`
          *,
          category:pos_categories(id, name)
        `)?.eq('gym_id', gymId)?.eq('alu', alu)?.eq('is_active', true)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createProduct(gymId, productData) {
    try {
      const { data, error } = await supabase?.from('pos_products')?.insert({ gym_id: gymId, ...productData })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateProduct(productId, updates) {
    try {
      const { data, error } = await supabase?.from('pos_products')?.update(updates)?.eq('id', productId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteProduct(productId) {
    try {
      const { error } = await supabase?.from('pos_products')?.update({ is_active: false })?.eq('id', productId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Credit Card Configurations
  async getCreditCardConfigs(gymId) {
    try {
      const { data, error } = await supabase?.from('credit_card_configs')?.select('*')?.eq('gym_id', gymId)?.eq('is_enabled', true);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createCreditCardConfig(gymId, configData) {
    try {
      const { data, error } = await supabase?.from('credit_card_configs')?.insert({ gym_id: gymId, ...configData })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateCreditCardConfig(configId, updates) {
    try {
      const { data, error } = await supabase?.from('credit_card_configs')?.update(updates)?.eq('id', configId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Transactions
  async createTransaction(gymId, transactionData, items) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate stock availability before creating transaction
      for (const item of items) {
        if (item?.product_id) {
          const { data: product, error: productError } = await supabase
            ?.from('pos_products')
            ?.select('stock_quantity, name')
            ?.eq('id', item?.product_id)
            ?.single();

          if (productError) throw new Error(`Failed to check stock for ${item?.product_name}`);
          
          if (product?.stock_quantity < item?.quantity) {
            throw new Error(`Insufficient stock for ${product?.name}. Available: ${product?.stock_quantity}, Requested: ${item?.quantity}`);
          }
        }
      }

      // Generate transaction number
      const { data: transactionNumber, error: numberError } = await supabase?.rpc('generate_transaction_number');

      if (numberError) throw numberError;

      // Create transaction
      const { data: transaction, error: transactionError } = await supabase?.from('pos_transactions')?.insert({
          gym_id: gymId,
          transaction_number: transactionNumber,
          staff_id: user?.id,
          ...transactionData
        })?.select()?.single();

      if (transactionError) throw transactionError;

      // Create transaction items
      const transactionItems = items?.map(item => ({
        transaction_id: transaction?.id,
        product_id: item?.product_id,
        product_name: item?.product_name,
        quantity: item?.quantity,
        unit_price: item?.unit_price,
        subtotal: item?.subtotal
      }));

      const { error: itemsError } = await supabase?.from('pos_transaction_items')?.insert(transactionItems);

      if (itemsError) throw itemsError;

      // Update product stock quantities
      for (const item of items) {
        if (item?.product_id) {
          const { error: stockError } = await supabase?.rpc('decrement_product_stock', {
            product_id: item?.product_id,
            quantity: item?.quantity
          });
          if (stockError) {
            console.error('Stock update error:', stockError);
            throw new Error(`Failed to update stock for ${item?.product_name}`);
          }
        }
      }

      return { data: transaction, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTransactions(gymId, filters = {}) {
    try {
      let query = supabase?.from('pos_transactions')?.select(`
          *,
          staff:user_profiles!pos_transactions_staff_id_fkey(id, full_name),
          member:user_profiles!pos_transactions_member_id_fkey(id, full_name)
        `)?.eq('gym_id', gymId);

      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }

      if (filters?.paymentMethod) {
        query = query?.eq('payment_method', filters?.paymentMethod);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getTransactionDetails(transactionId) {
    try {
      const { data, error } = await supabase?.from('pos_transactions')?.select(`
          *,
          staff:user_profiles!pos_transactions_staff_id_fkey(id, full_name),
          member:user_profiles!pos_transactions_member_id_fkey(id, full_name),
          items:pos_transaction_items(
            *,
            product:pos_products(id, name, category_id)
          )
        `)?.eq('id', transactionId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Low Stock Alerts
  async getLowStockProducts(gymId) {
    try {
      const { data, error } = await supabase?.from('pos_products')?.select(`
          *,
          category:pos_categories(id, name)
        `)?.eq('gym_id', gymId)?.eq('is_active', true)?.filter('stock_quantity', 'lte', 'low_stock_threshold')?.order('stock_quantity');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Report Generation Service
export const reportService = {
  async getReportTemplates() {
    try {
      const { data, error } = await supabase?.from('report_templates')?.select('*')?.eq('is_active', true)?.order('name');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async generateReport(gymId, templateId, dateRange) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: template, error: templateError } = await supabase?.from('report_templates')?.select('*')?.eq('id', templateId)?.single();

      if (templateError) throw templateError;

      // Generate report data based on template type
      let reportData = {};

      switch (template?.report_type) {
        case 'financial':
          reportData = await this.generateFinancialReport(gymId, dateRange);
          break;
        case 'membership':
          reportData = await this.generateMembershipReport(gymId, dateRange);
          break;
        case 'operations':
          reportData = await this.generateOperationsReport(gymId, dateRange);
          break;
        case 'inventory':
          reportData = await this.generateInventoryReport(gymId);
          break;
        default:
          reportData = { message: 'Report type not implemented' };
      }

      // Save generated report
      const { data: savedReport, error: saveError } = await supabase?.from('generated_reports')?.insert({
          gym_id: gymId,
          template_id: templateId,
          report_name: template?.name,
          report_type: template?.report_type,
          date_range_start: dateRange?.start,
          date_range_end: dateRange?.end,
          report_data: reportData,
          generated_by: user?.id
        })?.select()?.single();

      if (saveError) throw saveError;

      return { data: savedReport, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async generateFinancialReport(gymId, dateRange) {
    // Fetch transaction data
    const { data: transactions } = await supabase?.from('pos_transactions')?.select('*')?.eq('gym_id', gymId)?.gte('created_at', dateRange?.start)?.lte('created_at', dateRange?.end);

    const totalRevenue = transactions?.reduce((sum, t) => sum + parseFloat(t?.total), 0) || 0;
    const transactionCount = transactions?.length || 0;
    const cashTransactions = transactions?.filter(t => t?.payment_method === 'cash')?.length || 0;
    const cardTransactions = transactions?.filter(t => t?.payment_method === 'credit_card')?.length || 0;

    return {
      totalRevenue,
      transactionCount,
      averageTransaction: transactionCount > 0 ? totalRevenue / transactionCount : 0,
      paymentMethods: {
        cash: cashTransactions,
        creditCard: cardTransactions
      }
    };
  },

  async generateMembershipReport(gymId, dateRange) {
    // Placeholder - would integrate with membership data
    return {
      newMembers: 0,
      activeMembers: 0,
      churnedMembers: 0,
      retentionRate: 0
    };
  },

  async generateOperationsReport(gymId, dateRange) {
    // Placeholder - would integrate with operations data
    return {
      checkIns: 0,
      classAttendance: 0,
      staffActivity: 0
    };
  },

  async generateInventoryReport(gymId) {
    const { data: products } = await supabase?.from('pos_products')?.select('*')?.eq('gym_id', gymId)?.eq('is_active', true);

    const lowStockItems = products?.filter(p => p?.stock_quantity <= p?.low_stock_threshold) || [];
    const outOfStockItems = products?.filter(p => p?.stock_quantity === 0) || [];
    const totalValue = products?.reduce((sum, p) => sum + (p?.stock_quantity * parseFloat(p?.cost || 0)), 0) || 0;

    return {
      totalProducts: products?.length || 0,
      lowStockCount: lowStockItems?.length,
      outOfStockCount: outOfStockItems?.length,
      totalInventoryValue: totalValue,
      lowStockItems: lowStockItems?.map(p => ({ name: p?.name, stock: p?.stock_quantity }))
    };
  },

  async getGeneratedReports(gymId, filters = {}) {
    try {
      let query = supabase?.from('generated_reports')?.select(`
          *,
          template:report_templates(name, report_type),
          generated_by_user:user_profiles(full_name)
        `)?.eq('gym_id', gymId);

      if (filters?.reportType) {
        query = query?.eq('report_type', filters?.reportType);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};